import React, {Component, ReactNode} from 'react';
import Content from 'app/components/pages/articles/Content';
import ParticipatePromoButton from 'app/components/reusable/ParticipatePromoButton';
import {getId} from 'app/utils/articles/getId';
import ArticleIndexInterface from 'app/interfaces/articles/ArticleIndexInterface';
import type {ParticipationData} from 'app/interfaces/articles/ArticleInterface';
import {articlesStore} from 'app/store/global';
import SmartFullPageLoader from 'app/components/reusable/smart_loaders/SmartFullPageLoader';
import I18n from 'app/i18n';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import {ARTICLE_PROMOTION_OLD_TYPE} from 'app/utils/Consts';
import {pushAnalyticsEvent} from 'app/utils/analytics';

interface ArticleIndexProps {
    type: string
}

interface ArticleIndexState {
    bonusType: string | null
    content: string
    date: number | null
    expired: boolean
    id: string
    loading: boolean
    participation: ParticipationData | null
    promoId: number | null
    title: string
}

export default class ArticleIndex extends Component<ArticleIndexProps, ArticleIndexState> {
    state: ArticleIndexState = {
        bonusType: null,
        content: '',
        date: null,
        expired: false,
        id: getId(),
        loading: true,
        participation: null,
        promoId: null,
        title: ''
    };

    setData = (data: ArticleIndexInterface): void => {
        const {id: promoId, bonus_type: bonusType, expired, participation, title, date, content} = data;
        this.setState({
            bonusType: bonusType ?? null,
            content,
            date,
            expired: expired ?? false,
            loading: false,
            participation: participation ?? null,
            promoId,
            title
        }, () => this.trackArticleView());
    };

    componentDidMount(): void {
        this.refreshArticle();
    }

    componentDidUpdate(): void {
        const id = getId();

        if (this.state.id !== id) {
            this.setState({id}, this.refreshArticle);
        }
    }

    onClickBack = (): void => {
        history.back();
    };

    refreshArticle = (): void => {
        articlesStore.loadArticleByTypeAndId(this.props.type, this.state.id, this.setData);
    };

    isPromotion = (): boolean => this.props.type === ARTICLE_PROMOTION_OLD_TYPE || this.props.type === 'promotions';

    trackArticleView = (): void => {
        const {promoId} = this.state;

        if (this.isPromotion()) {
            pushAnalyticsEvent('promotion_view', {
                promotion_id: promoId,
                source: 'promotion_article'
            });
            return;
        }

        pushAnalyticsEvent('seo_article_view', {
            article_id: promoId,
            article_type: this.props.type
        });
    };

    render(): ReactNode {
        const {type} = this.props;
        const {promoId, bonusType, expired, participation, title, content, date} = this.state;
        const showParticipateButton = Boolean(type === ARTICLE_PROMOTION_OLD_TYPE && promoId && bonusType && !expired);
        const isParticipating = participation?.participating;

        return this.state.loading
            ? <SmartFullPageLoader/>
            : <>
                <div className="title">
                    <a
                        className="cursor-pointer"
                        onClick={this.onClickBack}
                    >
                        <SpriteIcon name="#svg-back" size={{height: 8, width: 5}}/>
                        {I18n.t('button_back_label')}
                    </a>
                    <h1 style={{fontSize: 24, marginBottom: 1}} dangerouslySetInnerHTML={{__html: title}}/>
                </div>
                <Content
                    articleId={promoId}
                    articleType={type}
                    date={date}
                    content={content}
                    trackInternalLinks={!this.isPromotion()}
                />
                {showParticipateButton && <div className="article__participate">
                    <ParticipatePromoButton
                        authPlacement="PROMO_ARTICLE"
                        className="article__participate-btn"
                        title={title}
                        promoId={promoId}
                        isParticipating={isParticipating}
                        onParticipated={this.refreshArticle}
                    />
                </div>}
            </>;
    }
}
